'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  quarter: string;
  title: string;
  description: string;
  milestone: string;
  icon: string;
}

export default function TimelineSection() {
  const [activeYear, setActiveYear] = useState<string>('2018');

  const timelineEvents: TimelineEvent[] = [
    {
      year: '2021',
      quarter: 'Year 1',
      title: 'Foundation & Food Security Focus',
      description: 'Focused on grocery drives and sponsorship programs. Hosted two grocery drives and delivered groceries to families who applied to our program, targeting food insecurity communities.',
      milestone: 'First grocery drives launched',
      icon: 'ShoppingCartIcon'
    },
    {
      year: '2022',
      quarter: 'Year 2',
      title: 'Curriculum Development & Testing',
      description: 'Developed an agricultural technology curriculum and tested it at HD Woodson HS twice. Aimed to transform STEM as a catalyst for fighting food insecurity and innovation.',
      milestone: 'Curriculum created and piloted',
      icon: 'AcademicCapIcon'
    },
    {
      year: '2023',
      quarter: 'Year 3',
      title: 'Prototyping & First Paid Program',
      description: 'Continued testing our agricultural technology curriculum and developed three smaller proof-of-concept vertical farms that informed a larger prototype. Launched our first paid pilot program with a vertical farm placement.',
      milestone: 'First paid pilot program',
      icon: 'BeakerIcon'
    },
    {
      year: '2024',
      quarter: 'Year 4',
      title: 'Largest Prototype & Validation',
      description: 'Created our largest prototype yet, which is now informing our design with our USC partnership and paid programs. Validated our concept and prepared for scale.',
      milestone: 'Concept validated at scale',
      icon: 'RocketLaunchIcon'
    },
    {
      year: '2025',
      quarter: 'Year 5',
      title: 'Nationwide Growth & Partnerships',
      description: 'Working with partners, stakeholders, and growing nationwide! Expanding our reach across multiple states with strategic partnerships and proven curriculum.',
      milestone: 'Nationwide expansion underway',
      icon: 'GlobeAmericasIcon'
    }
  ];

  const years = Array.from(new Set(timelineEvents.map(e => e.year)));
  const filteredEvents = timelineEvents.filter(e => e.year === activeYear);

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface via-background to-brand-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
            <Icon name="ClockIcon" size={20} className="text-accent" variant="solid" />
            <span className="text-sm font-semibold text-accent">Our Journey</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            5-Year Timeline of Growth & Impact
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From grocery drives to nationwide expansion-explore our journey from local food security initiatives to transforming STEM education across America.
          </p>
        </div>

        <div className="flex justify-center gap-3 mb-12 flex-wrap">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setActiveYear(year)}
              className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                activeYear === year
                  ? 'bg-brand-primary text-brand-primary-foreground shadow-brand-lg scale-105'
                  : 'bg-white text-text-secondary hover:bg-surface hover:shadow-brand-md'
              }`}
            >
              {year}
            </button>
          ))}
        </div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-brand-primary via-accent to-success hidden lg:block" />
          
          <div className="space-y-12">
            {filteredEvents.map((event, index) => (
              <div
                key={index}
                className={`flex flex-col lg:flex-row gap-8 items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                <div className="flex-1 lg:text-right">
                  {index % 2 === 0 && (
                    <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-brand-md hover:shadow-brand-lg transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4 lg:justify-end">
                        <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm font-bold rounded-full">
                          {event.quarter} {event.year}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{event.title}</h3>
                      <p className="text-text-secondary mb-4 leading-relaxed">{event.description}</p>
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 rounded-lg">
                        <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                        <span className="text-sm font-semibold text-success">{event.milestone}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative z-10 flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-accent rounded-full flex items-center justify-center shadow-brand-lg">
                    <Icon name={event.icon as any} size={32} className="text-white" variant="solid" />
                  </div>
                </div>

                <div className="flex-1">
                  {index % 2 !== 0 && (
                    <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-brand-md hover:shadow-brand-lg transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm font-bold rounded-full">
                          {event.quarter} {event.year}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{event.title}</h3>
                      <p className="text-text-secondary mb-4 leading-relaxed">{event.description}</p>
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 rounded-lg">
                        <Icon name="CheckCircleIcon" size={20} className="text-success" variant="solid" />
                        <span className="text-sm font-semibold text-success">{event.milestone}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-block rounded-2xl p-8 lg:p-12" style={{ backgroundColor: '#4F744A' }}>
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              The Journey Continues
            </h3>
            <p className="text-white/90 text-lg max-w-2xl mx-auto mb-6">
              From our first grocery drives to developing cutting-edge agricultural technology curriculum, every milestone represents our commitment to fighting food insecurity through education and innovation. Together, we're building a future where every young person has the tools to become a climate leader.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold text-white">5</div>
                <div className="text-sm text-white/90">Years of Growth</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold text-white">600+</div>
                <div className="text-sm text-white/90">Pounds Distributed</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold text-white">4</div>
                <div className="text-sm text-white/90">Partner Schools</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}